import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Theme, SxProps, Typography } from "@mui/material";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Close from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { DateTime } from "luxon";
import { gql } from "generated-graphql";
import {
  Issue,
  SubmissionReviewerPickerQuery,
} from "generated-graphql/graphql";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { getFullName } from "encamp-shared/src/utils/name";
import { v4 as uuidv4 } from "uuid";
import {
  useReviewerIssues,
  useDueDateWarningsByUserId,
} from "contexts/SubmissionFormContext";

export interface Reviewer {
  id: string;
  name: string;
  email: string;
  dueDate?: DateTime;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  isSigner?: boolean;
  isStaff: boolean;
  order: number;
}

interface ReviewersListProps {
  reviewers: Reviewer[];
  setReviewers?: (reviewers: Reviewer[]) => void;
  rowReordering?: boolean;
}

export function ReviewersEditList({
  reviewers,
  setReviewers,
  rowReordering,
}: ReviewersListProps) {
  const issues = useReviewerIssues();
  const dueDateWarningsByUserId = useDueDateWarningsByUserId();
  const reviewerIssues = useMemo(
    () => issues.filter((i) => i.modelName === "SubmissionReviewer"),
    [issues]
  );

  const getRowIssues = useCallback(
    (reviewer: Reviewer): Issue[] => {
      const issues = reviewerIssues.filter((i) => {
        return i.schemaStack?.some((s) => s.name === reviewer.id);
      });
      return issues;
    },
    [reviewerIssues]
  );

  const [reviewerList, setReviewerList] = useState<Reviewer[]>(
    [...reviewers]
      .sort((a, b) => a?.order && b?.order && a.order - b.order)
      .map((r) => ({
        ...r,
        __reorder__: r.name ?? "", // this is whats displayed in the little box when you drag and drop
      }))
  );

  const updateReviewer = (reviewerId: string, updates: Partial<Reviewer>) => {
    setReviewerList((prev) =>
      prev.map((r) => (r.id === reviewerId ? { ...r, ...updates } : r))
    );
  };

  const handleAddReviewer = () => {
    const newReviewer: Reviewer = {
      id: uuidv4(),
      name: "",
      email: "",
      isStaff: false,
      order: reviewerList.length + 1,
    };
    setReviewerList([...reviewerList, newReviewer]);
  };

  useEffect(() => {
    setReviewers?.(reviewerList);
  }, [reviewerList, setReviewers]);

  const columns: GridColDef<Reviewer>[] = useMemo(
    () => [
      {
        field: "reviewOrder",
        headerName: "",
        width: 60,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                height: "100%",
                pl: "20px",
                py: "10px",
              }}
            >
              <Typography variant="body2" sx={{ mt: 2 }}>
                {row.order}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "content",
        headerName: "",
        align: "center",
        display: "flex",
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <ReviewersListItem
              sx={{ py: "8px" }}
              value={row || null}
              disabledReviewers={reviewerList.filter((r) => r?.id !== null)}
              onChange={(selectedUser) => {
                updateReviewer(row.id, {
                  id: selectedUser?.id ?? "",
                  email: selectedUser?.email ?? "",
                  name: selectedUser?.name ?? "",
                });
              }}
            />
          );
        },
      },
      {
        field: "dueDate",
        headerName: "",
        display: "flex",
        flex: 0.55,
        renderCell: ({ row }) => {
          const cellIssues = getRowIssues(row).filter(
            (i) => i.key === "dueDate"
          );
          const hasError = cellIssues && cellIssues.length > 0;

          const warningMessage = dueDateWarningsByUserId[row.id];
          const hasWarning = !hasError && Boolean(warningMessage);

          return (
            <Box
              sx={{
                width: "100%",
                py: "8px",
                pb: hasError || hasWarning ? 0.5 : 0,
                "& .MuiTextField-root": { margin: 0 },
              }}
            >
              <DatePicker
                label="Due date"
                value={row.dueDate}
                onChange={(date) => {
                  updateReviewer(row.id, {
                    dueDate: date ?? undefined,
                  });
                }}
                slotProps={{
                  textField: {
                    error: !!hasError,
                    sx: {
                      width: "100%",
                      margin: 0,
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused": {
                          boxShadow: "none",
                        },
                        "&:hover": {
                          boxShadow: "none",
                        },
                      },
                      ...(hasWarning && {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "warning.main",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "warning.dark",
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "warning.main",
                        },
                      }),
                      "& .MuiInputBase-root": {
                        margin: 0,
                      },
                    },
                  },
                }}
              />
              {hasError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    px: 1.5,
                    pt: 0.5,
                    display: "block",
                    wordWrap: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {cellIssues[0].message}
                </Typography>
              )}
              {hasWarning && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    pt: 0.5,
                    display: "block",
                    color: "warning.dark",
                  }}
                >
                  {warningMessage}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        display: "flex",
        flex: 0.15,
        renderCell: ({ row }) => {
          return (
            <Button
              variant="text"
              color="inherit"
              sx={{ mt: 1, minWidth: "auto", py: "16px", pl: "20px" }}
              onClick={() => {
                setReviewerList((prev) => {
                  const filtered = prev.filter((r) => r.id !== row.id);

                  // If this was the last reviewer, add a default placeholder
                  if (filtered.length === 0) {
                    return [
                      {
                        id: uuidv4(),
                        name: "",
                        email: "",
                        order: 1,
                        isStaff: false,
                      },
                    ];
                  }

                  // Otherwise, reorder the remaining reviewers
                  return filtered.map((r, index) => {
                    r.order = index + 1;
                    return r;
                  });
                });
              }}
            >
              <Close />
            </Button>
          );
        },
      },
    ],
    [reviewerList, getRowIssues, dueDateWarningsByUserId]
  );

  return (
    <>
      <Box
        sx={{
          maxHeight: 375, // Max height for 5 rows (5 * 75px)
          overflowY: "auto",
          border: "none",
        }}
      >
        <DataGridPremium
          rows={reviewerList}
          columns={columns}
          rowReordering={rowReordering}
          onRowOrderChange={(params) => {
            setReviewerList((prev) => {
              const newList = [...prev];
              const oldIndex = params.oldIndex;
              const newIndex = params.targetIndex;
              const [movedItem] = newList.splice(oldIndex, 1);
              newList.splice(newIndex, 0, movedItem);
              // update the order of the reviewers
              newList.forEach((r, index) => {
                r.order = index + 1;
              });
              return newList;
            });
            setReviewers?.(reviewerList);
          }}
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          getRowHeight={() => "auto"}
          autoHeight={true}
          sx={{
            border: "none",
            "& .MuiDataGrid-row": {
              maxHeight: "none",
            },
            "& .MuiDataGrid-cell": {
              maxHeight: "none",
              display: "flex",
              alignItems: "flex-start",
              paddingTop: 0,
              paddingBottom: 0,
            },
            "& .MuiDataGrid-rowReorderCell": {
              paddingTop: "26px",
            },
            "& .MuiDataGrid-main": {
              "& .MuiDataGrid-columnHeaders": {
                display: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                "& .MuiDataGrid-row": {
                  // no row highlighting when we cant reorder them
                  "&:hover": rowReordering
                    ? {}
                    : { backgroundColor: "transparent" },
                  // Divider / space between rows
                  "& .MuiDataGrid-cell": {
                    border: "none",
                    padding: 0,
                    "&:focus": {
                      outline: "none",
                    },
                    "&:focus-within": {
                      outline: "none",
                    },
                  },
                },
              },
            },
            // Remove focus outline on cells
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within":
              {
                outline: "none",
              },
            // This is the row highlighting when you drag and drop
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent",
            },
          }}
        />
      </Box>
      {/* Add Additional Reviewer Button */}
      <Button
        startIcon={<PersonAdd />}
        onClick={handleAddReviewer}
        variant="text"
        color="primary"
        sx={{ alignSelf: "flex-start" }}
      >
        Additional reviewer
      </Button>
    </>
  );
}

function ReviewersListItem({
  value,
  onChange,
  disabledReviewers,
  sx,
}: {
  value: Reviewer | null;
  onChange: (selectedUser: Reviewer | null) => void;
  disabledReviewers?: Reviewer[];
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      sx={{
        ...sx,
        px: 2,
        width: "100%",
        "& .MuiAutocomplete-root": { margin: 0 },
      }}
    >
      <ReviewerPicker
        onChange={onChange}
        value={value}
        disabledReviewers={disabledReviewers}
      />
    </Box>
  );
}

interface ReviewerPickerProps {
  onChange: (reviewer: Reviewer | null) => void;
  value?: Reviewer | null;
  disabled?: boolean;
  disabledReviewers?: Reviewer[];
  staffOnly?: boolean;
  sx?: SxProps<Theme>;
}

const SUBMISSION_REVIEWER_PICKER_QUERY = gql(`
  query SubmissionReviewerPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...SubmissionReviewerPickerUser
      }
    }
  }
`);

const STAFF_REVIEWER_PICKER_QUERY = gql(`
  query StaffReviewerPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users: staffUsers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...SubmissionReviewerPickerUser
      }
    }
  }
`);

export function ReviewerPicker({
  onChange,
  value,
  disabled,
  disabledReviewers,
  staffOnly = false,
  sx,
}: ReviewerPickerProps) {
  return (
    <OmnisearchAutocomplete<SubmissionReviewerPickerQuery, any, Reviewer>
      dataQuery={
        staffOnly
          ? STAFF_REVIEWER_PICKER_QUERY
          : SUBMISSION_REVIEWER_PICKER_QUERY
      }
      getItems={(data) =>
        data?.users?.items.map((r, index) => ({
          id: r.id,
          name: r.person ? getFullName(r.person) : r.email,
          email: r.email,
          order: index + 1,
          isStaff: staffOnly,
        })) ?? []
      }
      getOptionLabel={(item) => {
        return item?.name !== item?.email
          ? `${item?.name} (${item?.email})`
          : item?.email;
      }}
      onChange={onChange}
      value={value || null}
      disabled={disabled}
      runInitialSearch={true}
      getOptionDisabled={(item) => {
        return disabledReviewers?.some((r) => r.id === item?.id) ?? false;
      }}
      sx={{ width: "100%", ...sx }}
      hideError={true}
    />
  );
}
